import {
  IconAlertTriangle,
  IconBuildingSkyscraper,
  IconCalendarStats,
  IconChartHistogram,
  IconFiles,
  IconLayoutDashboard,
  IconPhoto,
  IconReportAnalytics,
  IconSend,
  IconShieldLock,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import type { ComponentType } from 'react';

import type { AppTranslate, AppTranslationKey } from '@/i18n';
import { findInvestor, findProject } from '@/data/domain';

export interface NavItem {
  path: string;
  /**
   * A catalogue KEY rather than the label itself.
   *
   * These entries are module-level constants, resolved before any provider
   * exists, so a label stored here would be frozen in whatever language the
   * module happened to load in — and, once translated, would never change again
   * on a language switch. The key travels instead, and the component that
   * renders the entry translates it, which is what makes the sidebar, the page
   * headings and the browser tab all follow the selector.
   */
  labelKey: AppTranslationKey;
  icon: ComponentType<{ size?: number | string; className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  /** Short line under the label in the sidebar's expanded state. */
  hintKey: AppTranslationKey;
  /** Exact matching only — for an index route that would otherwise match everything. */
  end?: boolean;
}

export interface NavGroup {
  labelKey: AppTranslationKey;
  items: NavItem[];
}

/**
 * The internal platform's navigation.
 *
 * One registry drives the sidebar, the route table and the page headings, so a
 * page cannot exist without appearing in the navigation and a nav entry cannot
 * point at a route that was never registered. The document's functional
 * requirements are the organising principle, not an arbitrary menu order:
 * portfolio, then money, then people, then records, then the publication gate.
 */
export const PLATFORM_NAV: NavGroup[] = [
  {
    labelKey: 'nav.group.overview',
    items: [
      { path: '/dashboard', labelKey: 'nav.platform.dashboard', icon: IconLayoutDashboard, hintKey: 'nav.platform.dashboardHint', end: true },
      { path: '/opportunities', labelKey: 'nav.platform.opportunities', icon: IconBuildingSkyscraper, hintKey: 'nav.platform.opportunitiesHint' },
    ],
  },
  {
    labelKey: 'nav.group.delivery',
    items: [
      { path: '/projects', labelKey: 'nav.platform.projects', icon: IconChartHistogram, hintKey: 'nav.platform.projectsHint' },
      { path: '/schedule', labelKey: 'nav.platform.schedule', icon: IconCalendarStats, hintKey: 'nav.platform.scheduleHint' },
      { path: '/evidence', labelKey: 'nav.platform.evidence', icon: IconPhoto, hintKey: 'nav.platform.evidenceHint' },
    ],
  },
  {
    labelKey: 'nav.group.money',
    items: [
      { path: '/financials', labelKey: 'nav.platform.financials', icon: IconFiles, hintKey: 'nav.platform.financialsHint' },
      { path: '/contractors', labelKey: 'nav.platform.suppliers', icon: IconUsersGroup, hintKey: 'nav.platform.suppliersHint' },
    ],
  },
  {
    labelKey: 'nav.group.relationships',
    items: [
      { path: '/investors', labelKey: 'nav.platform.investors', icon: IconUsers, hintKey: 'nav.platform.investorsHint' },
    ],
  },
  {
    labelKey: 'nav.group.publishing',
    items: [
      { path: '/publications', labelKey: 'nav.platform.publications', icon: IconSend, hintKey: 'nav.platform.publicationsHint' },
      { path: '/reports', labelKey: 'nav.platform.reports', icon: IconReportAnalytics, hintKey: 'nav.platform.reportsHint' },
      { path: '/notifications', labelKey: 'nav.platform.notifications', icon: IconAlertTriangle, hintKey: 'nav.platform.notificationsHint' },
    ],
  },
  {
    labelKey: 'nav.group.administration',
    items: [
      { path: '/permissions', labelKey: 'nav.platform.permissions', icon: IconShieldLock, hintKey: 'nav.platform.permissionsHint' },
    ],
  },
];

/**
 * The investor portal's navigation. Four entries, matching the document's §12
 * table — and deliberately NOT the platform's navigation, because the portal is
 * a different product with a different audience and a different rule about what
 * may be shown.
 */
export const PORTAL_NAV: NavItem[] = [
  { path: '/portal', labelKey: 'nav.portal.projects', icon: IconLayoutDashboard, hintKey: 'nav.portal.projectsHint', end: true },
  { path: '/portal/updates', labelKey: 'nav.portal.updates', icon: IconSend, hintKey: 'nav.portal.updatesHint' },
  { path: '/portal/documents', labelKey: 'nav.portal.documents', icon: IconFiles, hintKey: 'nav.portal.documentsHint' },
  { path: '/portal/opportunities', labelKey: 'nav.portal.opportunities', icon: IconBuildingSkyscraper, hintKey: 'nav.portal.opportunitiesHint' },
];

/** Flattens the registry, for route generation and breadcrumb lookup. */
export const PLATFORM_NAV_FLAT: NavItem[] = PLATFORM_NAV.flatMap((group) => group.items);

/**
 * A human title for a path, for the header and breadcrumbs. Falls back to a
 * de-slugged segment so a detail page still reads as a noun rather than an id.
 */
export function navTitleFor(pathname: string, t: AppTranslate): string {
  const exact = PLATFORM_NAV_FLAT.find((item) => item.path === pathname);
  if (exact) return t(exact.labelKey);

  const section = PLATFORM_NAV_FLAT.filter(
    (item) => item.path !== '/' && pathname.startsWith(item.path),
  ).sort((a, b) => b.path.length - a.path.length)[0];
  return section ? t(section.labelKey) : t('nav.fallback');
}

/**
 * The page's own name, for the document title.
 *
 * Every route previously shared the title `escrowbuild.io`, so the browser tab
 * said the same thing on the dashboard, on a report and on the investor portal
 * — the one place in the interface where a page's name was absent. Eighteen
 * tabs open on eighteen screens were indistinguishable.
 *
 * A project's sub-tabs name the development as well as the tab, because
 * "Schedule" alone does not say whose schedule it is once it is out of the
 * browser's context.
 *
 * `t` is passed in rather than imported because this is a plain function, not a
 * hook: the title has to be re-derived on a language change, and only a caller
 * that read the active locale can know when that happened.
 */
export function pageTitle(pathname: string, t: AppTranslate): string {
  const projectMatch = /^\/projects\/([^/]+)(?:\/([^/]+))?$/.exec(pathname);
  if (projectMatch) {
    const project = findProject(projectMatch[1]);
    const tab = projectMatch[2];
    const tabLabel = PROJECT_TABS.find((entry) => entry.value === tab);
    if (project) return [project.name, tabLabel ? t(tabLabel.labelKey) : undefined].filter(Boolean).join(' · ');
    return tabLabel
      ? `${navTitleFor(pathname, t)} · ${t(tabLabel.labelKey)}`
      : navTitleFor(pathname, t);
  }

  const investorMatch = /^\/investors\/([^/]+)$/.exec(pathname);
  if (investorMatch) {
    const investor = findInvestor(investorMatch[1]);
    if (investor) return investor.name;
  }

  const portal = PORTAL_NAV.find((item) =>
    item.end ? item.path === pathname : pathname.startsWith(item.path),
  );
  if (portal) {
    // A portal project screen is named by the development, not by the tab.
    const portalProject = /^\/portal\/projects\/([^/]+)$/.exec(pathname);
    if (portalProject) return findProject(portalProject[1])?.name ?? t(portal.labelKey);
    return t(portal.labelKey);
  }

  // The sign-in screen is the entry point, so `/` names itself rather than
  // falling through to the catch-all below.
  if (pathname === '/' || pathname === '/login') return t('nav.signIn');

  // The provider's callback is a route with no registry entry, so without this
  // it reads from the catch-all below as "Project" — a tab that names the wrong
  // page while the visitor is watching it finish signing them in.
  if (pathname === '/auth/callback') return t('nav.signingIn');

  const exact = PLATFORM_NAV_FLAT.find((item) => item.path === pathname);
  if (exact) return t(exact.labelKey);

  return navTitleFor(pathname, t);
}

/**
 * The tab names a project screen can be on, in the order the shell shows them.
 *
 * The development's own name is the shell's `h1` and its first tab, so the
 * registry starts at the second one — a "Overview" tab beside a page already
 * titled with the development would be the shell naming itself twice.
 */
export const PROJECT_TABS: readonly { value: string; labelKey: AppTranslationKey }[] = [
  { value: 'schedule', labelKey: 'nav.tab.schedule' },
  { value: 'financials', labelKey: 'nav.tab.financials' },
  { value: 'evidence', labelKey: 'nav.tab.evidence' },
  { value: 'investors', labelKey: 'nav.tab.investors' },
  { value: 'publications', labelKey: 'nav.tab.publications' },
];

/**
 * The suffix every tab shares, so the product is identifiable in a tab strip
 * without the page name having to repeat it.
 */
export const TITLE_SUFFIX = 'escrowbuild.io';
